<template>
	<main>
		<TopBottomLayout>
			<h6>기업분석</h6>
			<div class="company_analysis_wrap">
				<section
					v-if="!isBannerLoading && getBannerMulti?.company_analysis && getBannerMulti.company_analysis.length > 0"
					class="c_responsive"
				>
					<SubSwiperSlide :data="getBannerMulti.company_analysis ?? []" />
				</section>
				<section>
					<ResponsiveLayout>
						<div class="title_box">
							<ExpandableTitleBar :title="`${selectedTab} Top${MAX_EXPOSURE_JPN}`" :has-view="false" />
							<p class="base_date">{{ dayjs(companyAnalysisData?.base_date).format('YYYY-MM-DD') }} 기준</p>
						</div>
					</ResponsiveLayout>
					<StickyTabBar
						:items="tabItems"
						:style="{ margin: '1rem 0 2.4rem' }"
						@update:category="handleClickRemove"
					></StickyTabBar>
					<ResponsiveLayout>
						<div class="content_box">
							<div class="select_box">
								<div class="tooltip">
									<img
										class="tooltip_mark"
										src="@/assets/img/common/question.svg"
										alt="도움말"
										@mousemove="
											deviceStore.getDevice === 'MO' ? (isTooltipOpened = !isTooltipOpened) : (isTooltipOpened = true)
										"
										@mouseout="isTooltipOpened = false"
									/>
									<div v-if="isTooltipOpened" class="tooltip_content">
										<h4>정렬방식</h4>
										<ul>
											<li v-for="(t, i) in tooltipContent" :key="i">
												<span>{{ t.title }}</span>
												<span>{{ t.content }}</span>
											</li>
										</ul>
									</div>
								</div>

								<select :value="Number($route.query.sort ?? 10)" class="c_select" @change="onChangeSort">
									<option value="10">10개씩 보기</option>
									<option value="25">25개씩 보기</option>
									<option value="50">50개씩 보기</option>
									<option value="100">100개씩 보기</option>
								</select>
							</div>
							<ListLayout v-if="companyAnalysisData">
								<ShortenBookmarkList
									v-for="(item, index) in companyAnalysisData?.company_analysis"
									:key="item.event_pk"
									:index="index"
									:data="item"
									indexed
									@update:book-mark-refetch="handleBookmarkRefetch"
								/>
								<MoreButton
									v-if="companyAnalysisData.company_analysis.length < companyAnalysisData?.count"
									@click="onChangeMore"
								/>
							</ListLayout>
						</div>
					</ResponsiveLayout>
				</section>
			</div>
		</TopBottomLayout>
	</main>
</template>

<script setup lang="ts">
// core
import { computed, inject, onBeforeMount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQueryClient } from '@tanstack/vue-query';
// lib
import dayjs from 'dayjs';

import { useGetCheckAuthApi } from '@/api/auth';
// api
import { useGetBannerMultiApi } from '@/api/banner';
import { useGetCompanyAnalysisInfiniteApi_JPN } from '@/api/companyAnalysis';
import MoreButton from '@/components/atom/button/MoreButton.vue';
import ExpandableTitleBar from '@/components/atom/tabBar/ExpandableTitleBar.vue';
import StickyTabBar from '@/components/atom/tabBar/StickyTabBar.vue';
import ListLayout from '@/components/layout/main/ListLayout.vue';
import ResponsiveLayout from '@/components/layout/main/ResponsiveLayout.vue';
// component
import TopBottomLayout from '@/components/layout/main/TopBottomLayout.vue';
import ShortenBookmarkList from '@/components/rankList/ShortenBookmarkList.vue';
import SubSwiperSlide from '@/components/swiper/SubSwiperSlide.vue';
// store
import { useDeviceStore } from '@/stores/device';

const MAX_EXPOSURE_JPN = inject('MAX_EXPOSURE_JPN');

// -----variable-----
const tabItems = ['시가총액', 'PER', 'PBR', 'ROE', '배당수익률'];
const tooltipContent = [
	{
		title: '시가총액',
		content: '시가총액이 높은 순으로 정렬됩니다.',
	},
	{
		title: 'PER',
		content: 'PER 낮은 순으로 우선 정렬되며, 음수 또는 계산이 불가한 종목은 시가총액 높은 순으로 정렬됩니다.',
	},
	{
		title: 'PBR',
		content: 'PBR 낮은 순으로 우선 정렬되며, 음수 또는 계산이 불가한 종목은 시가총액 높은 순으로 정렬됩니다.',
	},
	{
		title: 'ROE',
		content: 'ROE 높은 순으로 우선 정렬되며, 음수 또는 계산이 불가한 종목은 시가총액 높은 순으로 정렬됩니다.',
	},
	{
		title: '배당수익률',
		content: '배당수익률이 높은 순으로 우선 정렬되며, 음수 또는 계산이 불가한 종목은 시가총액 높은 순으로 정렬됩니다.',
	},
];

const route = useRoute();
const router = useRouter();
const deviceStore = useDeviceStore();
const queryClient = useQueryClient();

const moreNumber = ref(route.query.sort ? Number(route.query.sort) : 10);
const isTooltipOpened = ref(false);

const selectedTab = computed(() => {
	return route.query.tab ?? '시가총액';
});

// -----api-----
const { refetch: checkAuthRefetch } = useGetCheckAuthApi(
	computed(() => {
		return {
			num: 'ONE',
			student: false,
		};
	})
);
const { data: getBannerMulti, isLoading: isBannerLoading } = useGetBannerMultiApi({ type: 'company_analysis' });
const {
	data: companyAnalysisData,
	refetch: refetchCompanyAnalysisData,
	fetchNextPage: fetchNextPageCompanyAnalysisData,
} = useGetCompanyAnalysisInfiniteApi_JPN(
	computed(() => {
		return {
			tab: String(selectedTab.value),
			view: moreNumber.value,
		};
	})
);

// -----function-----
const onChangeSort = (event: Event) => {
	const target = event.target as HTMLSelectElement;
	const newSortValue = Number(target.value);
	moreNumber.value = newSortValue; // 정렬 변경시 현재 표시 개수도 업데이트
	router.push({ query: { ...route.query, sort: newSortValue } });
};
const onChangeMore = async () => {
	fetchNextPageCompanyAnalysisData();
};
const handleBookmarkRefetch = async () => {
	await refetchCompanyAnalysisData();
};
const handleClickRemove = async (tab: string) => {
	queryClient.removeQueries({
		queryKey: ['/v1/front/usa/getCompanyAnalysis', { tab: tab, view: moreNumber.value }],
	});
};

// -----composition-----
watch(
	() => route.query.tab,
	() => {
		moreNumber.value = 10;
	}
);

onBeforeMount(async () => {
	const res = await checkAuthRefetch();
	if (!res.data?.success) {
		router.replace(`/subscribe/jpn/not`);
	}
});
</script>

<style scoped lang="scss">
.company_analysis_wrap {
	display: flex;
	flex-direction: column;
	gap: 2.4rem;
}
.title_box {
	display: flex;
	flex-direction: column;
	gap: 1rem;
	.base_date {
		color: var(--color-base-light);
		font-size: var(--font-s-small);
		font-weight: var(--font-w-light);
		line-height: normal;
	}
}
.content_box {
	display: flex;
	flex-direction: column;
	gap: 2.4rem;
	margin-bottom: 1.6rem;
	.select_box {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		gap: 0.4rem;
		position: relative;
		.tooltip {
			padding: 0.9rem;
			.tooltip_mark {
				width: 2.4rem;
				height: 2.4rem;
				cursor: pointer;
			}
			.tooltip_content {
				display: flex;
				flex-direction: column;
				gap: 1rem;
				position: absolute;
				top: 5rem;
				right: 0;
				z-index: 1;
				min-width: 24rem;
				max-width: 32rem;
				background-color: #2c3646;
				padding: 1.6rem;
				font-size: var(--font-s-mid);
				font-weight: var(--font-w-light);
				line-height: 1.9rem;
				border-radius: 0.8rem;
				box-shadow: 4px 4px 10px 10px #0000004d;
				@media screen and (max-width: 1280px) {
					position: absolute;
					right: 0;
				}
				h4 {
					font-weight: var(--font-w-semi-bold);
				}
				ul {
					display: flex;
					flex-direction: column;
					gap: 1rem;
					li {
						display: flex;
						align-items: center;
						gap: 1.6rem;
						span:nth-child(1) {
							width: 7rem;
							color: #ffa800;
						}
						span:nth-child(2) {
							width: 20rem;
						}
					}
				}
			}
		}
	}
}
.btn_more {
	display: flex;
	align-items: center;
	gap: 0.4rem;
	width: 100%;
	height: auto;
	padding: 1.3rem 1.6rem;
}
</style>
